import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { useBackendApi } from '@/hooks/fetch';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { txToast } from '@/components/TxToaster';
import { ActivityChart, type ActivitySeries } from '../monitorShared/Charts';
import {
    AlertsTable, DataTable, MonitorHeader, SearchBox, StatCard, StatusBanner, StatusLabel,
    type HistoryRange, type LiveRange, type MonitorAlert, type MonitorStatus, type Severity, type ViewMode,
} from '../monitorShared/MonitorChrome';
import { fmtAgo, fmtBytes, fmtDateTime, fmtNum } from '../monitorShared/format';


// =============================================
//  Types de l'API /vanta/statebags/*
// =============================================
type Point = { t: number; writesPs: number; writtenBps: number; replicatedBps: number; keys: number; alerts: number };
type KeyRow = {
    id: string;
    key: string;
    type: 'PLAYER' | 'ENTITY' | 'GLOBAL' | 'OTHER';
    writer: string | null;
    writers: { res: string; writes: number; bytes: number }[];
    unattributed: number;
    status: Severity;
    writes: number;
    ratePs: number;
    bytes: number;
    bps: number;
    avg: number;
    max: number;
    replicatedPct: number;
    nils: number;
    bags: number;
    hookOnly: boolean;
};
type ResRow = { name: string; writes: number; ratePs: number; bytes: number; bps: number; max: number; keys: number; replicatedPct: number };
type LargeWrite = { bag: string; key: string; size: number; replicated: boolean; t: number; preview: string };
type InventoryRow = { type: string; key: string; bags: number; bytes: number; max: number; maxBag: string };
type Inventory = {
    ts: number;
    error?: string;
    bags?: number;
    players?: number;
    entities?: number;
    totalKeys?: number;
    totalBytes?: number;
    durationMs?: number;
    rows: InventoryRow[];
};
type DataResp = {
    ts: number;
    range: LiveRange;
    windowCount: number;
    status: MonitorStatus;
    cards: { writesPs: number; writtenBps: number; replicatedBps: number; replicatedPct: number; keys: number; alerts: number; critical: number };
    series: Point[];
    keys: KeyRow[];
    resources: ResRow[];
    unattributedWrites: number;
    largest: LargeWrite[];
    alerts: MonitorAlert[];
    thresholds: Record<string, number>;
    scan: { requestedAt: number | null; hasResult: boolean };
    history: { sizeBytes: number; retentionDays: number };
    error?: string;
};
type HistoryResp = {
    days: number;
    from: number | null;
    to: number | null;
    buckets: number;
    series: Point[];
    keys: { type: string; key: string; writes: number; ratePs: number; bytes: number; bps: number; max: number; replicatedPct: number }[];
    error?: string;
};

const ACTIVITY_SERIES: ActivitySeries<Point>[] = [
    { key: 'writesPs', label: 'Writes', fmt: (v) => `${fmtNum(v)} /s` },
    { key: 'writtenBps', label: 'Written', fmt: (v) => `${fmtBytes(v)}/s` },
    { key: 'replicatedBps', label: 'Replicated', fmt: (v) => `${fmtBytes(v)}/s` },
    { key: 'keys', label: 'Keys written', fmt: (v) => fmtNum(v) },
];

const mono = (s: string) => <span className="font-mono text-foreground">{s}</span>;
const bagLabel = (t: string) => <span className="text-[11px] font-semibold tracking-wider text-muted-foreground">{t}</span>;


/**
 * State Bag Monitor : écritures de state bags (Player / Entity / GlobalState), tailles, réplication.
 */
export default function StateBagMonitorPage() {
    const [mode, setMode] = useState<ViewMode>('live');
    const [range, setRange] = useState<LiveRange>('5m');
    const [histRange, setHistRange] = useState<HistoryRange>('1');
    const [livePaused, setLivePaused] = useState(false);
    const [tab, setTab] = useState('keys');
    const [keyFilter, setKeyFilter] = useState('');
    const [search, setSearch] = useState('');

    const dataApi = useBackendApi<DataResp>({ method: 'GET', path: '/vanta/statebags/data' });
    const historyApi = useBackendApi<HistoryResp>({ method: 'GET', path: '/vanta/statebags/history' });
    const inventoryApi = useBackendApi<{ inventory: Inventory | null; requestedAt: number | null }>({ method: 'GET', path: '/vanta/statebags/inventory' });
    const scanApi = useBackendApi({ method: 'POST', path: '/vanta/statebags/inventory-scan' });

    const live = useSWR(mode === 'live' ? `/vanta/statebags/data?range=${range}` : null, async () => {
        const data = await dataApi({ queryParams: { range } });
        if (!data) throw new Error('empty_response');
        if (data.error) throw new Error(data.error);
        if (!data.cards || !data.series || !data.keys) throw new Error('invalid_response');
        return data;
    }, { refreshInterval: livePaused ? 0 : 5000, revalidateOnFocus: false, keepPreviousData: true });
    const hist = useSWR(mode === 'history' ? `/vanta/statebags/history?days=${histRange}` : null, async () => {
        const data = await historyApi({ queryParams: { days: histRange } });
        if (!data) throw new Error('empty_response');
        if (data.error) throw new Error(data.error);
        return data;
    }, { revalidateOnFocus: false, refreshInterval: 60_000 });
    const scanPending = !!live.data?.scan.requestedAt;
    const inv = useSWR(mode === 'live' && tab === 'inventory' ? '/vanta/statebags/inventory' : null, async () => {
        const data = await inventoryApi({});
        if (!data) throw new Error('empty_response');
        return data;
    }, { refreshInterval: scanPending ? 2000 : 0, revalidateOnFocus: false });

    const d = live.data;
    const h = hist.data;
    const series = mode === 'live' ? d?.series ?? [] : h?.series ?? [];
    const spark = (k: keyof Point) => series.map((p) => p[k] as number);

    const cards = useMemo(() => {
        if (mode === 'live' && d) return d.cards;
        if (mode === 'history' && h && h.series.length) {
            const n = h.series.length;
            const avg = (k: keyof Point) => h.series.reduce((a, p) => a + (p[k] as number), 0) / n;
            const written = avg('writtenBps');
            const repl = avg('replicatedBps');
            return {
                writesPs: avg('writesPs'),
                writtenBps: written,
                replicatedBps: repl,
                replicatedPct: written ? Math.round(repl / written * 100) : 0,
                keys: h.keys.length,
                alerts: h.series.reduce((a, p) => a + p.alerts, 0),
                critical: 0,
            };
        }
        return null;
    }, [mode, d, h]);

    const filteredKeys = useMemo(() => {
        if (!d) return [];
        const f = keyFilter.toLowerCase();
        if (!f) return d.keys;
        return d.keys.filter((k) => k.key.toLowerCase().includes(f) || k.type.toLowerCase() === f
            || k.writers.some((w) => w.res.toLowerCase().includes(f)));
    }, [d, keyFilter]);

    const searchResults = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!d || q.length < 2) return [];
        const out: { kind: string; label: string; detail: string; go: () => void }[] = [];
        for (const k of d.keys) if (k.key.toLowerCase().includes(q)) {
            out.push({ kind: `${k.type} key`, label: k.key, detail: `${fmtNum(k.ratePs)} writes/s · max ${fmtBytes(k.max)} · ${k.writer ?? 'unattributed'}`, go: () => { setKeyFilter(k.key); setTab('keys'); } });
        }
        for (const r of d.resources) if (r.name.toLowerCase().includes(q)) {
            out.push({ kind: 'Resource', label: r.name, detail: `${fmtNum(r.ratePs)} writes/s · ${r.keys} keys`, go: () => { setKeyFilter(r.name); setTab('keys'); } });
        }
        for (const w of d.largest) if (w.bag.toLowerCase().includes(q) || w.preview.toLowerCase().includes(q)) {
            out.push({ kind: 'Write', label: `${w.bag} · ${w.key}`, detail: fmtBytes(w.size), go: () => setTab('largest') });
        }
        return out.slice(0, 200);
    }, [d, search]);

    const requestScan = () => {
        scanApi({
            data: {},
            toastLoadingMessage: 'Requesting inventory scan…',
            genericHandler: { successMsg: 'Scan requested: the monitor resource runs it within 5 s.' },
            success: () => {
                live.mutate();
                inv.mutate();
            },
            error: (msg) => txToast.error(msg),
        });
    };
    const inventory = inv.data?.inventory ?? null;

    return (
        <div className="w-full space-y-4">
            <MonitorHeader
                title="State Bag Monitor"
                updatedAt={d?.ts ?? null}
                mode={mode} setMode={setMode}
                range={range} setRange={setRange}
                histRange={histRange} setHistRange={setHistRange}
                livePaused={livePaused} setLivePaused={setLivePaused}
            />
            <StatusBanner
                feature="statebags"
                status={d?.status}
                historySize={d?.history?.sizeBytes}
                onChanged={() => live.mutate()}
            />
            {live.error && mode === 'live' && (
                <div className="rounded-lg border border-[#d03b3b] px-4 py-2 text-sm text-[#d03b3b]">
                    Failed to load data: {(live.error as Error).message}
                </div>
            )}

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
                <StatCard label="Writes /s" value={fmtNum(cards?.writesPs ?? 0)} unit="/s"
                    sub={d && mode === 'live' && d.unattributedWrites ? `${fmtNum(d.unattributedWrites)} not attributed` : 'all bags'}
                    spark={spark('writesPs')} />
                <StatCard label="Written" value={fmtBytes(cards?.writtenBps ?? 0)} unit="/s"
                    sub="msgpack size of values" spark={spark('writtenBps')} />
                <StatCard label="Replicated" value={fmtBytes(cards?.replicatedBps ?? 0)} unit="/s"
                    sub={`${cards?.replicatedPct ?? 0}% of written`} spark={spark('replicatedBps')} />
                <StatCard label="Keys" value={String(cards?.keys ?? 0)}
                    sub={mode === 'live' ? 'distinct keys written in range' : 'tracked in history'} spark={spark('keys')} />
                <StatCard label="Alerts" value={String(cards?.alerts ?? 0)}
                    sub={mode === 'live' ? `${cards?.critical ?? 0} critical · last 60s` : 'raised in period'}
                    spark={spark('alerts')}
                    tone={cards?.critical ? 'critical' : cards?.alerts ? 'warning' : undefined} />
            </div>

            <div className="rounded-lg border bg-card p-4">
                <div className="mb-2 text-sm font-semibold">Activity</div>
                <ActivityChart data={series} series={ACTIVITY_SERIES} withDate={mode === 'history'} />
            </div>

            {mode === 'history' ? (
                <div className="space-y-2">
                    <div className="text-xs text-muted-foreground">
                        {h ? `${h.buckets} buckets · ${fmtDateTime(h.from)} → ${fmtDateTime(h.to)} · top keys per 5 min bucket` : 'Loading history…'}
                    </div>
                    <DataTable
                        rows={h?.keys ?? []}
                        rowKey={(k) => `${k.type}:${k.key}`}
                        defaultSort="bytes"
                        empty="No history recorded yet."
                        columns={[
                            { key: 'key', label: 'Key', render: (k) => mono(k.key) },
                            { key: 'type', label: 'Bag', render: (k) => bagLabel(k.type) },
                            { key: 'writes', label: 'Writes', align: 'right', render: (k) => fmtNum(k.writes) },
                            { key: 'ratePs', label: 'Rate', align: 'right', render: (k) => `${fmtNum(k.ratePs, 3)}/s` },
                            { key: 'bytes', label: 'Bytes', align: 'right', render: (k) => fmtBytes(k.bytes) },
                            { key: 'max', label: 'Max', align: 'right', render: (k) => fmtBytes(k.max) },
                            { key: 'replicatedPct', label: 'Replicated', align: 'right', render: (k) => `${k.replicatedPct}%` },
                        ]}
                    />
                </div>
            ) : (
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="h-auto flex-wrap justify-start">
                        <TabsTrigger value="keys">Keys</TabsTrigger>
                        <TabsTrigger value="resources">Resources</TabsTrigger>
                        <TabsTrigger value="largest">Largest writes</TabsTrigger>
                        <TabsTrigger value="inventory">Inventory scan</TabsTrigger>
                        <TabsTrigger value="alerts">Alerts{d?.cards.alerts ? ` (${d.cards.alerts})` : ''}</TabsTrigger>
                        <TabsTrigger value="search">Search</TabsTrigger>
                    </TabsList>

                    <TabsContent value="keys" className="space-y-2">
                        <SearchBox value={keyFilter} onChange={setKeyFilter} placeholder="Filter by key, writer or bag type (player, entity, global)" />
                        <DataTable<KeyRow>
                            rows={filteredKeys}
                            rowKey={(k) => k.id}
                            defaultSort="status"
                            empty="No state bag write in this range."
                            columns={[
                                { key: 'key', label: 'Key', render: (k) => mono(k.key) },
                                { key: 'type', label: 'Bag', render: (k) => <>{bagLabel(k.type)}{k.bags > 1 && <span className="ml-1 text-muted-foreground">×{k.bags >= 512 ? '512+' : k.bags}</span>}</> },
                                {
                                    key: 'writer', label: 'Writer', value: (k) => k.writer ?? '',
                                    render: (k) => (
                                        <span title={k.writers.map((w) => `${w.res}: ${w.writes}`).join('\n') + (k.unattributed ? `\nunattributed: ${k.unattributed}` : '')}>
                                            {k.writer ? mono(k.writer) : <span className="text-muted-foreground">client / unhooked</span>}
                                            {k.writers.length > 1 && <span className="text-muted-foreground"> +{k.writers.length - 1}</span>}
                                            {k.writer && k.unattributed > 0 && <span className="text-muted-foreground"> · {k.unattributed} unattributed</span>}
                                        </span>
                                    ),
                                },
                                { key: 'status', label: 'Status', value: (k) => (k.status === 'critical' ? 2 : k.status === 'warning' ? 1 : 0), render: (k) => <StatusLabel status={k.status} /> },
                                { key: 'writes', label: 'Writes', align: 'right', render: (k) => fmtNum(k.writes) },
                                { key: 'ratePs', label: 'Rate', align: 'right', render: (k) => `${fmtNum(k.ratePs, 3)}/s` },
                                { key: 'bytes', label: 'Bytes', align: 'right', render: (k) => fmtBytes(k.bytes) },
                                { key: 'avg', label: 'Avg', align: 'right', render: (k) => fmtBytes(k.avg) },
                                { key: 'max', label: 'Max', align: 'right', render: (k) => <span className={d && k.max >= d.thresholds.valueWarnBytes ? 'text-[#b47c00] dark:text-[#fab219]' : ''}>{fmtBytes(k.max)}</span> },
                                { key: 'replicatedPct', label: 'Replicated', align: 'right', render: (k) => `${k.replicatedPct}%` },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="resources" className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                            Writers identified by the hook (server-side Lua writes through Entity().state, Player().state and GlobalState).
                            Client writes and writes from non-hooked or JS/C# resources are listed as unattributed
                            {d ? ` (${fmtNum(d.unattributedWrites)} in range)` : ''}.
                        </p>
                        <DataTable<ResRow>
                            rows={d?.resources ?? []}
                            rowKey={(r) => r.name}
                            defaultSort="bytes"
                            empty="No attributed write yet (requires the hook)."
                            columns={[
                                { key: 'name', label: 'Resource', render: (r) => mono(r.name) },
                                { key: 'writes', label: 'Writes', align: 'right', render: (r) => fmtNum(r.writes) },
                                { key: 'ratePs', label: 'Rate', align: 'right', render: (r) => `${fmtNum(r.ratePs, 3)}/s` },
                                { key: 'bytes', label: 'Bytes', align: 'right', render: (r) => fmtBytes(r.bytes) },
                                { key: 'bps', label: 'B/s', align: 'right', render: (r) => fmtBytes(r.bps) },
                                { key: 'max', label: 'Max', align: 'right', render: (r) => fmtBytes(r.max) },
                                { key: 'keys', label: 'Keys', align: 'right' },
                                { key: 'replicatedPct', label: 'Replicated', align: 'right', render: (r) => `${r.replicatedPct}%` },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="largest">
                        <DataTable<LargeWrite>
                            rows={d?.largest ?? []}
                            rowKey={(w) => `${w.bag}|${w.key}|${w.t}|${w.size}`}
                            defaultSort="size"
                            empty="No write in this range."
                            columns={[
                                { key: 'size', label: 'Size', align: 'right', render: (w) => fmtBytes(w.size) },
                                { key: 'bag', label: 'Bag', render: (w) => mono(w.bag) },
                                { key: 'key', label: 'Key', render: (w) => mono(w.key) },
                                { key: 'replicated', label: 'Replicated', value: (w) => (w.replicated ? 1 : 0), render: (w) => (w.replicated ? 'yes' : 'no') },
                                { key: 't', label: 'When', render: (w) => fmtAgo(w.t) },
                                { key: 'preview', label: 'Value', render: (w) => <span className="block max-w-xl truncate font-mono text-muted-foreground" title={w.preview}>{w.preview}</span> },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="inventory" className="space-y-2">
                        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            <Button size="xs" variant="outline-muted" onClick={requestScan} disabled={scanPending}>
                                {scanPending ? 'Scan in progress…' : 'Run inventory scan'}
                            </Button>
                            <span>Reads every key of GlobalState, connected players and networked entities (GetStateBagKeys). Runs once, spread over several ticks.</span>
                        </div>
                        {inventory?.error && <div className="text-sm text-[#d03b3b]">{inventory.error}</div>}
                        {inventory && !inventory.error && (
                            <div className="text-xs text-muted-foreground">
                                Scanned {fmtAgo(inventory.ts)} · {inventory.bags} bags ({inventory.players} players, {inventory.entities} entities)
                                · {fmtNum(inventory.totalKeys ?? 0)} keys · {fmtBytes(inventory.totalBytes ?? 0)} stored · {inventory.durationMs} ms
                            </div>
                        )}
                        <DataTable<InventoryRow>
                            rows={inventory?.rows ?? []}
                            rowKey={(r) => `${r.type}:${r.key}`}
                            defaultSort="bytes"
                            empty={inventory ? 'No key found.' : 'No scan yet.'}
                            columns={[
                                { key: 'key', label: 'Key', render: (r) => mono(r.key) },
                                { key: 'type', label: 'Bag', render: (r) => bagLabel(r.type) },
                                { key: 'bags', label: 'Bags', align: 'right' },
                                { key: 'bytes', label: 'Total size', align: 'right', render: (r) => fmtBytes(r.bytes) },
                                { key: 'avg', label: 'Avg', align: 'right', value: (r) => (r.bags ? r.bytes / r.bags : 0), render: (r) => fmtBytes(r.bags ? r.bytes / r.bags : 0) },
                                { key: 'max', label: 'Max', align: 'right', render: (r) => fmtBytes(r.max) },
                                { key: 'maxBag', label: 'Largest in', render: (r) => <span className="font-mono text-muted-foreground">{r.maxBag}</span> },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="alerts">
                        <AlertsTable alerts={d?.alerts ?? []} />
                    </TabsContent>

                    <TabsContent value="search" className="space-y-2">
                        <SearchBox value={search} onChange={setSearch} placeholder="Search keys, resources, bags, values…" />
                        {search.trim().length >= 2 && (
                            <div className="divide-y rounded-lg border bg-card">
                                {searchResults.length === 0 && <div className="px-4 py-6 text-center text-sm text-muted-foreground">No match.</div>}
                                {searchResults.map((r, i) => (
                                    <button key={i} type="button" onClick={r.go}
                                        className="flex w-full items-baseline gap-3 px-4 py-2 text-left text-sm hover:bg-muted">
                                        <span className="w-28 shrink-0 text-xs uppercase tracking-wider text-muted-foreground">{r.kind}</span>
                                        <span className="font-mono">{r.label}</span>
                                        <span className="ml-auto truncate text-xs text-muted-foreground">{r.detail}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            )}
            {d && mode === 'live' && (
                <div className="text-xs text-muted-foreground">
                    {d.windowCount} reports in range · refreshed every 5 s
                    {d.status.resourceCollecting === false && ' · change handler stopped in the monitor resource'}
                </div>
            )}
        </div>
    );
}
